import axios from "axios";
import { Base64 } from "js-base64";

import { formatUSD, formatTimestamp } from "../helpers/formatters";
import { removeTradesDuplicates } from "../helpers/removeTradesDuplicates";

import { ETickerKind, ETimeframes } from "../enums";
import {
  IDexTrade,
  ISmartTrade,
  IDexChartTrade,
  IBalanceResponse,
  IAnnouncementsResponse,
  ICoin,
  IInfoResponse,
  IWalletInfoResponse,
  ITopWalletsResponseItem,
  IDexBigTrade,
} from "../interfaces/common";
import {
  ITicker,
  IFetchTickersPayload,
  ITrendingItem,
  IWalletPairsResponse,
  IRatesResponse,
  ILiquidityResponse,
  IPriceSnapshot,
  ITokenWithCoin,
  ITokenInfo,
  IChainInfo,
  IChainTrendingCoin,
  ITokenCoin,
} from "../interfaces/kattana";
import { ICovalentItem } from "../interfaces/covalent";

export interface IKattanaApi {
  balancesEndpoint: string;
  chartEndpoint: string;
  tradesEndpoint: string;
  smartTradesEndpoint: string;
  tickersEndpoint: string;
  trendingEndpoint: string;
  announcementsEndpoint: string;
  ratesEndpoint: string;
  topWalletsEndpoint: string;
  walletPairsEndpoint: string;
  liquidityEndpoint: string;
  tokensEndpoint: string;
  ordersEndpoint: string;
  overlayEndpoint: string;
  bigTradesEndpoint: string;
  coinsEndpoint: string;

  bigTrades: (value: string, limit: string, network?: string) => Promise<IDexBigTrade[] | null>;
  coins: () => Promise<ICoin[] | null>;
  coin: (slug: string, market: boolean, abortController: AbortController) => Promise<ICoin | null>;
  balances: (networkId: number, wallet: string, v2?: boolean) => Promise<IBalanceResponse | null>;
  trades: (network: string, pairAddress: string, since?: number) => Promise<IDexTrade[] | null>;
  rates: () => Promise<IRatesResponse | null>;
  trending: (abortController: AbortController) => Promise<ITrendingItem[] | null>;
  announcements: (abortController: AbortController) => Promise<IAnnouncementsResponse | []>;
  chart: (protocol: string, timeframe: ETimeframes, pair: string, startTime: number, endTime: number, limit: number, converted: string, flipped: boolean, precision: number, poolCreated: number, liquidityFilter: number,) => Promise<IDexChartTrade[] | null>;
  tickersByIds: (ids: string, options: any) => Promise<ITicker[]>;
  tickers: (payload: IFetchTickersPayload, options?: any) => Promise<ITicker[] | null>;
  coinPoolInfo: (pairAddress: string, controller: AbortController) => Promise<IInfoResponse | null>;
  walletTrades: (wallet: string, pairAddress: string, network: string, abortController: AbortController) => Promise<IDexTrade[] | null>;
  smartTrades: (wallet: string, network: string, period: 'day' | 'week' | 'month', abortController: AbortController) => Promise<Record<string, ISmartTrade[]> | null>;
  topTraders: (pairAddress: string, network: string, timeframe: string) => Promise<ITopWalletsResponseItem[] | []>;
  overlayWalletTrades: (wallet: string, network: string, pairAddress: string, from: number, to: number) => Promise<any>;
  fetchOverlayLimitOrders: (wallet: string, network: string, pairAddress: string) => Promise<any>;
  fetchOthersOverlayLimitOrders: (wallet: string, srcAddress: string, dstAddress: string, baseDecimals: number, quoteDecimals: number) => Promise<any>;
  walletPairs: (pairAddress: string, period: string) => Promise<IWalletPairsResponse[] | null>;
  walletInfo: (pairAddress: string, network: string, wallet: string, timeframe: string) => Promise<IWalletInfoResponse | []>;
  tickerByNetworkAndBase: (network: string, baseAddress: string) => Promise<ITicker[] | []>;
  liquidity: (network: string, pairAddress: string, duration: string) => Promise<ILiquidityResponse | null>;
  tokensPerNetwork: (network: string, params: ITokensPerNetworkPayload, abortController: AbortController) => Promise<ITokenWithCoin[] | null>;
  tokenInfo: (network: string, address: string, options?: any) => Promise<ITokenInfo | null>;
  tokenById: (id: string | number, options?: any) => Promise<ITokenInfo | null>;
  tokenBalanceForWallet: (network: string, token: string, wallet: string) => Promise<string | null>;
  chainInfo: (network: string, options?: any) => Promise<IChainInfo | null>;
  chainTrending: (network: string, options?: any) => Promise<Record<string, IChainTrendingCoin> | null>;
  createOrder: (wallet: string, payload: any) => Promise<any>;
  newPairs: () => Promise<ITicker [] | null>;
  tickerYesterday: (chain: string, address: string) => Promise<IPriceSnapshot [] | null>;
  tickerByNetworkBaseAndQuote: (network: string, input: string, output: string) => Promise<ITicker | null>;
}

interface ITokensPerNetworkPayload {
  query?: string
  page?: number
}


export class KattanaApi implements IKattanaApi {
  balancesEndpoint: string;
  chartEndpoint: string;
  tradesEndpoint: string;
  smartTradesEndpoint: string;
  tickersEndpoint: string;
  trendingEndpoint: string;
  announcementsEndpoint: string;
  ratesEndpoint: string;
  topWalletsEndpoint: string;
  walletPairsEndpoint: string;
  liquidityEndpoint: string;
  tokensEndpoint: string;
  chainEndpoint: string;
  ordersEndpoint: string;
  overlayEndpoint: string;
  bigTradesEndpoint: string;
  coinsEndpoint: string;

  constructor(apiGateway: string) {
    if (!apiGateway) {
      apiGateway = 'https://api.kattana.trade';
    }

    this.balancesEndpoint = `${apiGateway}/balances`;
    this.chartEndpoint = `${apiGateway}/chart`;
    this.tradesEndpoint = `${apiGateway}/trades`;
    this.smartTradesEndpoint = `${apiGateway}/smart-trades`;
    this.tickersEndpoint = `${apiGateway}/tickers`;
    this.trendingEndpoint = `${apiGateway}/trending`;
    this.announcementsEndpoint = `${apiGateway}/announcements`;
    this.ratesEndpoint = `${apiGateway}/rates`;
    this.topWalletsEndpoint = `${apiGateway}/top-wallets`;
    this.walletPairsEndpoint = `${apiGateway}/top-pairs`;
    this.liquidityEndpoint = `${apiGateway}/liquidity`;
    this.tokensEndpoint = `${apiGateway}/tokens`;
    this.chainEndpoint = `${apiGateway}/chain`;
    this.ordersEndpoint = `${apiGateway}/orders`;
    this.overlayEndpoint = `${apiGateway}/overlays`;
    this.bigTradesEndpoint = `${apiGateway}/big-trades`;
    this.coinsEndpoint = `${apiGateway}/coins`;
  }

  /**
   * Fetch list of big trades
   * @param value - price filter
   * @param limit - amount of trades
   * @returns 
   */
  public bigTrades = async (value: string, limit: string, network?: string): Promise<IDexBigTrade[] | null> => {
    try {
      const url = network ?
        `${this.bigTradesEndpoint}/${value}/${limit}?network=${network}`
        : `${this.bigTradesEndpoint}/${value}/${limit}`;
      const { data } = await axios.get(url);
    
      return data
    } catch (error) {
      console.warn(error);
      return null;
    }
  }

  /**
   * Fetch list of coins
   * list of coins or null
   */
  public coins = async (): Promise<ICoin[] | null> => {
    try {
      const { data } = await axios.get(this.coinsEndpoint);
    
      return data
    } catch (error) {
      console.warn(error);
      return null;
    }
  }

  /**
   * Fetch a specific coin with pairs 
   * @param slug 
   * @returns 
   */
  public coin = async (slug: string, market: boolean, abortController: AbortController): Promise<ICoin | null> => {
    try {
      const url = `${this.coinsEndpoint}/${slug}${market ? '?load=market' : ''}`;
      const { data } = await axios.get(url, {
        signal: abortController.signal,
      });
      return data
    } catch (error) {
      console.warn(error);
      return null;
    }
  }

  /**
   * Fetch balances
   */
  public balances = async (networkId: number, wallet: string, v2?: boolean): Promise<IBalanceResponse | null> => {
    try {
      const url = `${this.balancesEndpoint}/${networkId}/${wallet}${v2 ? '/v2' : ''}`;
      const { data } = await axios.get(url, { timeout: 20000 });
      if (data.error) {
        throw new Error(data.error_message);
      }

      if (data) {
        return data.items.map((item: ICovalentItem) => ({
          balance: Number(item.balance) / 10 ** item.contract_decimals,
          tokenInfo: item,
        }));
      }
      return null;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

  /**
   * Fetch trades
   * Add buy boolean
   */
  public trades = async (network: string, pairAddress: string, since?: number): Promise<IDexTrade[] | null> => {
    try {
      const url = `${this.tradesEndpoint}/${network}/${pairAddress}`;

      const params: any = {};

      if (since) {
        params.since = since;
      }

      const { data } = await axios.get(url, { params });

      return removeTradesDuplicates(data);
    } catch (e) {
      console.warn(e);
      return null;
    }
  };

  /**
   * Get rates
   */
  public rates = async (): Promise<IRatesResponse | null> => {
    try {
      const { data } = await axios.get(this.ratesEndpoint);
      return data;
    } catch (e) {
      console.warn(e);
      return null;
    }
  };

  /**
   * gets hot tickers
   */
  public trending = async (abortController: AbortController): Promise<ITrendingItem[] | null> => {
    try {
      const res = await axios(this.trendingEndpoint, {
        signal: abortController.signal,
      });
      return res.data;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

  /**
   * gets hot tickers
   */
  public announcements = async (abortController: AbortController): Promise<IAnnouncementsResponse | []> => {
    try {
      const res = await axios(this.announcementsEndpoint, {
        signal: abortController.signal,
      });
      return res.data;
    } catch (err) {
      console.warn(err);
      return [];
    }
  };

  /**
   * Fetch candles
   */
  public chart = async (
    protocol: string,
    timeframe: ETimeframes,
    pair: string,
    startTime: number,
    endTime: number,
    limit: number,
    converted: string,
    flipped: boolean,
    precision: number,
    poolCreated: number,
    liquidityFilter: number,
  ): Promise<IDexChartTrade[] | null> => {
    try {
      const url = `${this.chartEndpoint}/${protocol}/${pair}/${timeframe}/${startTime}/${endTime}/${limit}`;

      const params: any = {};

      if (converted) {
        params.converted = converted;
      }

      if (flipped) {
        params.flipped = true;
      }

      if (precision) {
        params.precision = precision;
      }

      if (liquidityFilter) {
        params.liquidityFilter = liquidityFilter;
      }

      params.poolCreated = poolCreated;

      const { data } = await axios.get(url, { params });

      return data;
    } catch (e) {
      console.warn(e);
      return null;
    }
  };

  /**
   * Seperate query for fetch by ids only
   * @param ids
   * @param options
   * @returns
   */
  public tickersByIds = async (ids: string, options: any): Promise<ITicker[]> => {
    try {
      const { data } = await axios.get(this.tickersEndpoint, {
        params: {
          ids,
        },
        ...options,
      });

      return data;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  /**
   * Fetch tickers for exchange
   * @param exchange
   */
  public tickers = async (payload: IFetchTickersPayload, options?: any): Promise<ITicker[] | null> => {
    try {
      const {
        searchRaw,
        id,
        exchanges,
        exchange,
        sort,
        page,
        kind,
        address,
        mobile,
        chain,
      } = payload;
      const search = searchRaw ? searchRaw.toLocaleUpperCase() : undefined;

      const { data } = await axios.get(this.tickersEndpoint, {
        params: {
          id: id && id > 0 ? id : undefined,
          exchanges:
            exchanges && exchanges.length > 0
              ? Base64.encodeURI(exchanges.join(","))
              : undefined,
          exchange:
            !(exchanges && exchanges.length > 0) && exchange
              ? Base64.encodeURI(exchange)
              : undefined,
          search: search ? Base64.encodeURI(search) : undefined,
          sort: sort ? Base64.encodeURI(`${sort.name}|${sort.order}`) : undefined,
          pairAddress: address ? Base64.encodeURI(address) : undefined,
          page,
          kind: kind === ETickerKind.ALL ? undefined : kind,
          sizeMod: mobile ? 'mobile' : undefined,
          chain: chain ? chain : undefined
        },
        ...options,
      });

      return data;
    } catch (e) {
      console.warn(e);
      return [];
    }
  };

  /**
   * Fetch a specific ticker with pool
   * @param baseAddress
   * @param pairAddress
   */
  public coinPoolInfo = async (pairAddress: string, controller: AbortController): Promise <IInfoResponse | null> => {
    try {
      const { data } = await axios.get(this.tickersEndpoint, {
        params: {
          search: Base64.encode(pairAddress),
        },
        signal: controller.signal,
      });
      const [ticker] = data;
      if (ticker) {
        const {
          base_reserve,
          quote_reserve,
          liquidity,
          name,
          pool_created,
          volume,
        } = ticker as ITicker;
        const [base, quote] = name.split("/");
        const info: any = {};
        if (volume) {
          info["Daily volume"] = formatUSD(Math.round(volume));
        }
        if (pool_created) {
          info["Pool created"] = formatTimestamp(pool_created);
        }
        info["Pool liquidity"] = formatUSD(Math.round(liquidity));
        info[`Pooled ${base}`] = base_reserve.toFixed(0);
        info[`Pooled ${quote}`] = quote_reserve.toFixed(0);

        return info;
      }

      return {};
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

  /**
   * Fetch DEX trades
   * @param wallet
   * @param pairAddress
   * @param protocol
   * @returns
   */
  public walletTrades = async (
    wallet: string,
    pairAddress: string,
    network: string,
    abortController: AbortController
  ): Promise<IDexTrade[] | null> => {
    try {
      const res = await axios(`${this.tradesEndpoint}/${network}/${pairAddress}`, {
        params: { wallet },
        signal: abortController.signal,
      });

      return res.data as IDexTrade[];
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

  /**
   * Fetch smart DEX trades for specific period by wallet and network
   * @param wallet
   * @param network
   * @param period
   * @returns
   */
  public smartTrades = async (
    wallet: string,
    network: string,
    period: 'day' | 'week' | 'month',
    abortController: AbortController
  ): Promise<Record<string, ISmartTrade[]> | null> => {
    try {
      const res = await axios(
        `${this.smartTradesEndpoint}/${network}/${wallet}/${period}`,
        {
          signal: abortController.signal,
        }
      );

      return res.data as Record<string, ISmartTrade[]>;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

  public topTraders = async (pairAddress: string, network: string, timeframe: string): Promise<ITopWalletsResponseItem[] | []> => {
    try {
      const res = await axios(`${this.topWalletsEndpoint}/${network}/${pairAddress}/${timeframe}`)
      return res.data
    } catch (err) {
      console.warn(err)
      return []
    }
  }

  public overlayWalletTrades = async (wallet: string, network: string, pairAddress: string, from: number, to: number) => {
    try {
      const res = await axios(`${this.overlayEndpoint}/wallet_trades/${wallet}/${network}/${pairAddress}`, {
        params: {
          from,
          to
        }
      })
      return res.data
    } catch (err) {
      console.warn(err)
      return []
    }
  }

  public fetchOverlayLimitOrders = async (wallet: string, network: string, pairAddress: string) => {
    try {
      const res = await axios(`${this.overlayEndpoint}/limit_orders/${wallet}/${network}/${pairAddress}`)
      return res.data
    } catch (err) {
      console.warn(err)
      return []
    }
  }

  public fetchOthersOverlayLimitOrders = async (wallet: string, srcAddress: string, dstAddress: string, baseDecimals: number, quoteDecimals: number) => {
    try {
      const res = await axios(`${this.overlayEndpoint}/other_limits/${wallet}/${srcAddress}/${dstAddress}/${baseDecimals}/${quoteDecimals}`)
      return res.data
    } catch (err) {
      console.warn(err)
      return []
    }
  }

  /**
   * Fetch other pairs of this wallet
   * @param pairAddress 
   * @param period 
   * @returns 
   */
  public walletPairs = async (pairAddress: string, period: string): Promise<IWalletPairsResponse[] | null> => {
    try {
      const res = await axios(`${this.walletPairsEndpoint}/${pairAddress}/${period}`)
      return res.data as IWalletPairsResponse[];
    } catch (err) {
      console.warn(err)
      return []
    }
  }

  public walletInfo = async (pairAddress: string, network: string, wallet: string, timeframe: string): Promise <IWalletInfoResponse | []> => {
    try {
      const res = await axios(`${this.topWalletsEndpoint}/${network}/${pairAddress}/details/${wallet}/${timeframe}`)
      return res.data
    } catch (err) {
      console.warn(err)
      return []
    }
  }

  public tickerByNetworkAndBase = async (network: string, baseAddress: string): Promise<ITicker[] | []> => {
    try {
      const res = await axios(`${this.tickersEndpoint}/${network}/${baseAddress}`)
      return res.data
    } catch (err) {
      console.warn(err)
      return []
    }
  }

  public liquidity = async (network: string, pairAddress: string, duration: string): Promise<ILiquidityResponse | null> => {
    try {
      const res = await axios(`${this.liquidityEndpoint}/${network}/${pairAddress}/${duration}`)
      return res.data
    } catch (err) {
      console.warn(err)
      return null
    }
  }

  /**
   * Used to fetch tokens per network
   * @param network - ETH, BSC, POLYGON
   * @param params - accepts optional query, page
   * @returns 
   */
  public tokensPerNetwork = async (network: string, params: ITokensPerNetworkPayload, abortController: AbortController): Promise<ITokenWithCoin[] | null> => {
    try {
      const res = await axios.get(`${this.tokensEndpoint}/${network}`, {
        params,
        signal: abortController.signal
      })
      return res.data as ITokenWithCoin[]
    } catch (err) {
      console.warn(err)
      return null
    }
  }

  /**
   * Used to fetch tokens per network
   * @param network - ETH, BSC, POLYGON
   * @param params - accepts optional query, page
   * @returns 
   */
  public featuredTokensPerNetwork = async (network: string, abortController: AbortController): Promise<ITokenCoin[] | null> => {
    try {
      const res = await axios.get(`${this.tokensEndpoint}/${network}/featured`, {
        signal: abortController.signal
      })
      return res.data as ITokenCoin[]
    } catch (err) {
      console.warn(err)
      return null
    }
  }

  /**
  * Used to fetch info about token
  * @param network - ETH, BSC, POLYGON
  * @param address - token address
  */
  public tokenInfo = async (network: string, address: string, options?: any): Promise<ITokenInfo | null> => {
    try {
      const res = await axios.get(`${this.tokensEndpoint}/${network}/${address}`, {
        ...options
      })
      return res.data as ITokenInfo
    } catch (err) {
      console.warn(err)
      return null
    }
  }

  /**
  * Used to fetch info about token
  * @param id - numeric id of this token
  */
  public tokenById = async (id: string | number, options?: any): Promise<ITokenInfo | null> => {
    try {
      const res = await axios.get(`${this.tokensEndpoint}/id/${id}`, {
        ...options
      })
      return res.data as ITokenInfo
    } catch (err) {
      console.warn(err)
      return null
    }
  }

  /**
  * Used to fetch info about network
  * @param network - ETH, BSC, POLYGON
  */
  public chainInfo = async (network: string, options?: any): Promise<IChainInfo | null> => {
    try {
      const res = await axios.get(`${this.chainEndpoint}/${network}`, {
        ...options
      })
      return res.data as IChainInfo
    } catch (err) {
      console.warn(err)
      return null
    }
  }

  /**
  * Used to fetch trending coins for specific network
  * @param network - ETH, BSC, POLYGON
  */
  public chainTrending = async (network: string, options?: any): Promise<Record<string, IChainTrendingCoin> | null> => {
    try {
      const res = await axios.get(`${this.chainEndpoint}/${network}/trending`, {
        ...options
      })
      return res.data as Record<string, IChainTrendingCoin>
    } catch (err) {
      console.warn(err)
      return null
    }
  }

  public createOrder = async (wallet: string, payload: any) => {
    try {
      const res = await axios.post(`${this.ordersEndpoint}/${wallet}/create`, payload);
      return res.data;
    } catch (err) {
      console.warn(err);
      return null;
    }
  }

  public newPairs = async (): Promise<ITicker [] | null> => {
    try {
      const res = await axios.get(`${this.tickersEndpoint}?sort=pool_created%3ADESC`);
      return res.data;
    } catch (err) {
      console.warn(err);
      return null;
    }
  }

  public tickerYesterday = async (chain: string, address: string): Promise<IPriceSnapshot [] | null> => {
    try {
      const res = await axios.get(`${this.tickersEndpoint}/${chain}/${address}/yesterday`);
      return res.data;
    } catch (err) {
      console.warn(err);
      return null;
    }
  }

  public tickerByNetworkBaseAndQuote = async (
    network: string,
    input: string,
    output: string
  ): Promise<ITicker | null> => {
    try {
      const res = await axios.get(`${this.tickersEndpoint}/${network}/${input}/${output}`);
      return res.data;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };

  public tokenBalanceForWallet = async (
    network: string,
    address: string,
    wallet: string
  ): Promise<string | null> => {
    try {
      const res = await axios.get(`${this.tokensEndpoint}/${network}/${address}/balance/${wallet}`);
      return res.data.balance;
    } catch (err) {
      console.warn(err);
      return null;
    }
  };
}
